// src/components/geniehireadmin/services/accLvlService.js

import axios from "axios";
import Cookies from "js-cookie";
import host from "../../../global.jsx"; // adjust the relative path if needed

const api = axios.create({
  baseURL: host.replace(/\/$/, ""),
});

const getAuthHeaders = () => {
  const accessToken = Cookies.get("accessToken");
  if (!accessToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }
  return { Authorization: `Bearer ${accessToken}` };
};

/* Individual endpoints (you can keep them or delete if you only use the parallel fetch) */
export const fetchOrganizationData = async (organisationId) => {
  if (!organisationId) throw new Error("Organization ID is missing.");
  if (organisationId.length !== 24 || !/^[0-9a-fA-F]+$/.test(organisationId)) {
    throw new Error("Invalid organization ID format.");
  }

  const { data } = await api.get(`/geniehireadmin/organisation/${organisationId}`, {
    headers: getAuthHeaders(),
  });
  return data;
};

export const fetchJobPostings = async (organisationId) => {
  const { data } = await api.get(`/geniehireadmin/jobdescriptions/${organisationId}`, {
    headers: getAuthHeaders(),
  });
  return data;
};

export const fetchResumeParsingData = async (organisationId) => {
  const { data } = await api.get(
    `/geniehireadmin/organisation/${organisationId}/usagefreqbyresumeparsing`,
    { headers: getAuthHeaders() }
  );
  return data;
};

export const fetchInterviewData = async (organisationId) => {
  const { data } = await api.get(
    `/geniehireadmin/organisation/${organisationId}/usagefreqbyinterview`,
    { headers: getAuthHeaders() }
  );
  return data;
};

/* Extend the current plan: new end date + countable add-ons + boolean toggles */
export const extendPlan = async (organisationId, { end_date, addons, boolean_features }) => {
  if (!organisationId) throw new Error("Organization ID is missing.");
  const { data } = await api.post(
    `/geniehireadmin/organisation/${organisationId}/extend-plan`,
    {
      end_date,
      addons: addons || {},
      boolean_features: boolean_features || {},
    },
    { headers: getAuthHeaders() }
  );
  return data;
};

/* Convert a trial account to a new Paid plan */
export const convertToPaid = async (organisationId, { end_date, countable_features, boolean_features }) => {
  if (!organisationId) throw new Error("Organization ID is missing.");
  const { data } = await api.post(
    `/geniehireadmin/organisation/${organisationId}/convert-to-paid`,
    {
      end_date,
      countable_features: countable_features || {},
      boolean_features: boolean_features || {},
    },
    { headers: getAuthHeaders() }
  );
  return data;
};

/* MAIN FUNCTION – fetches everything in parallel (recommended) */
export const fetchAllAccountLevelData = async (organisationId) => {
  // ---- Validation ----
  if (!organisationId) {
    throw new Error("Organization ID is required.");
  }
  if (organisationId.length !== 24 || !/^[0-9a-fA-F]+$/.test(organisationId)) {
    throw new Error("Invalid organization ID format.");
  }

  // ---- Parallel requests ----
  try {
    const [userData, jobPostingsData, resumeParseData, interviewData] = await Promise.all([
      fetchOrganizationData(organisationId),
      fetchJobPostings(organisationId),
      fetchResumeParsingData(organisationId),
      fetchInterviewData(organisationId),
    ]);

    return {
      userData,
      jobPostingsData,
      resumeParseData,
      interviewData,
    };
  } catch (error) {
    // Make error messages user-friendly
    if (error.response) {
      const { status } = error.response;
      if (status === 401) throw new Error("Unauthorized. Please log in again.");
      if (status === 404) throw new Error("Organization not found.");
      if (status === 422) throw new Error("Invalid request – bad organization ID.");
    }
    throw new Error(error.message || "Failed to load account data");
  }
};
